import { apiClient } from '@/shared/api';

import type {
  ExternalProviderConfigRequest,
  ExternalProviderConfigResponse,
  GovernedPricePage,
  GovernedPriceResponse,
  PriceLifecycleAuditPage,
  PriceLineRequest,
  PriceLineResponse,
  PriceProtectionPolicyRequest,
  PriceProtectionPolicyResponse,
  PriceRejectRequest,
  PricingRulePage,
  PricingRuleRequest,
  PricingRuleResponse,
  PricingRuleUpdateRequest,
  ResolvedPriceResponse,
  SalesChannelRequest,
  SalesChannelResponse,
  SalesChannelUpdateRequest,
  TenantPricingConfigRequest,
  TenantPricingConfigResponse,
} from '../model/types';

const BASE = '/api/v1/catalog/pricing';

// ── Config ──────────────────────────────────────────────────────────────────
export const getPricingConfig = async (): Promise<TenantPricingConfigResponse> =>
  (await apiClient.get<TenantPricingConfigResponse>(`${BASE}/config`)).data;

export const upsertPricingConfig = async (
  body: TenantPricingConfigRequest,
): Promise<TenantPricingConfigResponse> =>
  (await apiClient.put<TenantPricingConfigResponse>(`${BASE}/config`, body)).data;

// ── Rules ─────────────────────────────────────────────────────────────────────
export interface ListPricingRulesParams {
  productId?: string;
  ruleType?: string;
  page?: number;
  size?: number;
}

export const listPricingRules = async (
  params: ListPricingRulesParams = {},
): Promise<PricingRulePage> =>
  (await apiClient.get<PricingRulePage>(`${BASE}/rules`, { params })).data;

export const createPricingRule = async (
  body: PricingRuleRequest,
): Promise<PricingRuleResponse> =>
  (await apiClient.post<PricingRuleResponse>(`${BASE}/rules`, body)).data;

export const getPricingRule = async (ruleId: string): Promise<PricingRuleResponse> =>
  (await apiClient.get<PricingRuleResponse>(`${BASE}/rules/${ruleId}`)).data;

export const updatePricingRule = async (
  ruleId: string,
  body: PricingRuleUpdateRequest,
): Promise<PricingRuleResponse> =>
  (await apiClient.put<PricingRuleResponse>(`${BASE}/rules/${ruleId}`, body)).data;

export const deactivatePricingRule = async (ruleId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/rules/${ruleId}`);
};

// ── External provider configs ─────────────────────────────────────────────────
export const listProviderConfigs = async (): Promise<ExternalProviderConfigResponse[]> =>
  (await apiClient.get<ExternalProviderConfigResponse[]>(`${BASE}/provider-configs`)).data;

export const createProviderConfig = async (
  body: ExternalProviderConfigRequest,
): Promise<ExternalProviderConfigResponse> =>
  (await apiClient.post<ExternalProviderConfigResponse>(`${BASE}/provider-configs`, body)).data;

export const updateProviderConfig = async (
  configId: string,
  body: ExternalProviderConfigRequest,
): Promise<ExternalProviderConfigResponse> =>
  (await apiClient.put<ExternalProviderConfigResponse>(`${BASE}/provider-configs/${configId}`, body))
    .data;

export const deactivateProviderConfig = async (configId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/provider-configs/${configId}`);
};

// ── Approvals ─────────────────────────────────────────────────────────────────
export const listPriceApprovals = async (
  params: { page?: number; size?: number } = {},
): Promise<GovernedPricePage> =>
  (await apiClient.get<GovernedPricePage>(`${BASE}/approvals`, { params })).data;

export const approvePrice = async (priceId: string): Promise<GovernedPriceResponse> =>
  (await apiClient.post<GovernedPriceResponse>(`${BASE}/approvals/${priceId}/approve`)).data;

export const rejectPrice = async (
  priceId: string,
  body: PriceRejectRequest = {},
): Promise<GovernedPriceResponse> =>
  (await apiClient.post<GovernedPriceResponse>(`${BASE}/approvals/${priceId}/reject`, body)).data;

// ── Protection policy + audit ─────────────────────────────────────────────────
export const getPriceProtectionPolicy = async (): Promise<PriceProtectionPolicyResponse> =>
  (await apiClient.get<PriceProtectionPolicyResponse>(`${BASE}/protection-policy`)).data;

export const upsertPriceProtectionPolicy = async (
  body: PriceProtectionPolicyRequest,
): Promise<PriceProtectionPolicyResponse> =>
  (await apiClient.put<PriceProtectionPolicyResponse>(`${BASE}/protection-policy`, body)).data;

export const getPriceLifecycleAudit = async (
  params: { productId?: string; page?: number; size?: number } = {},
): Promise<PriceLifecycleAuditPage> =>
  (await apiClient.get<PriceLifecycleAuditPage>(`${BASE}/lifecycle-audit`, { params })).data;

// ── Sales channels (Phase 2b/3) ─────────────────────────────────────────────
export const listSalesChannels = async (): Promise<SalesChannelResponse[]> =>
  (await apiClient.get<SalesChannelResponse[]>(`${BASE}/sales-channels`)).data;

export const createSalesChannel = async (
  body: SalesChannelRequest,
): Promise<SalesChannelResponse> =>
  (await apiClient.post<SalesChannelResponse>(`${BASE}/sales-channels`, body)).data;

export const updateSalesChannel = async (
  code: string,
  body: SalesChannelUpdateRequest,
): Promise<SalesChannelResponse> =>
  (await apiClient.put<SalesChannelResponse>(`${BASE}/sales-channels/${code}`, body)).data;

// ── Price lines + resolution (Phase 2b/3) ───────────────────────────────────
export const submitPriceLine = async (body: PriceLineRequest): Promise<PriceLineResponse> =>
  (await apiClient.post<PriceLineResponse>(`${BASE}/price-lines`, body)).data;

export const listPriceLines = async (productId: string): Promise<PriceLineResponse[]> =>
  (await apiClient.get<PriceLineResponse[]>(`${BASE}/price-lines`, { params: { productId } })).data;

export interface ResolvePriceParams {
  productId: string;
  channelCode?: string;
  quantity?: number;
  at?: string;
}
export const resolvePrice = async (params: ResolvePriceParams): Promise<ResolvedPriceResponse> =>
  (await apiClient.get<ResolvedPriceResponse>(`${BASE}/price-resolution`, { params })).data;
